<template>
  <div class="tags-container">
    <div v-for="(tag, index) of tags" :key="index" class="tag-item">
      {{ tag }}
      <div class="tag-delete" @click="removeTag(tag)">
        <Icon v-if="editable" name="close" size="small" />
      </div>
    </div>

    <form v-if="editable" class="tag-form" @submit.prevent="createTag">
      <div v-if="input_visible" class="tag-input-form">
        <el-input ref="input" v-model="input_content" class="tag-input" />

        <div class="tag-button">
          <el-button type="primary" :disabled="is_disabled" @click="createTag"> 追加 </el-button>

          <el-button @click="hideInput">キャンセル</el-button>
        </div>
      </div>

      <el-button
        v-if="is_display_button"
        class="tag-button-show-input"
        :disabled="is_add_button_disabled"
        @click="showInput"
      >
        <Icon name="plus" space="right" />
        追加
      </el-button>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import Icon from '@/client/components/_atoms/Icon.vue'
import { trimWithInvisibleChars } from '@/client/utils/string'

export default defineComponent({
  components: { Icon },
  props: {
    value: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    editable: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number,
      required: false
    },
    type: {
      type: String as PropType<'hashtag' | 'keyword'>,
      required: false,
      default: 'hashtag'
    },
    snsType: {
      type: String as PropType<'twitter' | 'instagram' | 'tiktok'>,
      required: false,
      default: 'twitter'
    },
    maxItems: {
      type: Number,
      required: false
    }
  },
  data() {
    return {
      input_visible: false,
      input_content: ''
    }
  },
  computed: {
    tags: {
      get(): string[] {
        return this.value
      },
      set(value: string[]) {
        this.$emit('input', value)
      }
    },
    is_display_button(): boolean {
      return this.editable && !this.input_visible
    },
    is_add_button_disabled(): boolean {
      // maxItemsが設定されている場合は、現在のタグ数が上限以上の場合は無効化
      if (this.maxItems !== undefined && this.tags.length >= this.maxItems) {
        return true
      }
      return false
    },
    is_disabled(): boolean {
      return !trimWithInvisibleChars(this.input_content)
    }
  },
  methods: {
    /**
     * 入力を表示
     */
    showInput(): void {
      this.input_visible = true
      this.$nextTick(() => {
        const input = this.$refs.input as HTMLInputElement | undefined
        if (input) input.focus()
      })
    },
    /**
     * インプットを非表示
     */
    hideInput(): void {
      this.input_visible = false
      this.input_content = ''
    },
    /**
     * タグを変更
     */
    createTag(): void {
      let new_tag = trimWithInvisibleChars(this.input_content)

      if (!new_tag) {
        return
      }

      if (this.type === 'hashtag' && this.snsType !== 'twitter') {
        new_tag = new_tag.replace('＃', '#')

        // TODO: ハッシュタグの広告ポストが無効になるのでいったん暫定対応でコメントアウトにする
        if (new_tag[0] !== '#') {
          new_tag = '#' + new_tag
        }
      }

      const tag_length = this.tags.reduce((length, tag) => length + tag.length, 0)

      if (this.maxLength !== undefined && tag_length + new_tag.length > this.maxLength) {
        this.$notify({
          title: `文字数合計を${this.maxLength}文字以下にしてください。`,
          message: '',
          customClass: 'danger'
        })
        return
      }

      const is_duplicate = this.value.some(tag => tag === new_tag)

      if (is_duplicate) {
        const title = this.type === 'hashtag' ? 'すでに登録済みのキーワードです。' : 'すでに登録済みのキーワードです。'

        this.$notify({
          title: title,
          message: '',
          customClass: 'danger'
        })
        return
      }

      this.tags = [...this.tags, new_tag]
      this.hideInput()
    },
    /**
     * タグを削除
     */
    removeTag(removed_tag: string): void {
      this.tags = this.tags.filter(tag => tag !== removed_tag)
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.tag-item {
  display: inline-block;
  padding: 0 $spacing-unit;
  margin-right: $spacing-unit;
  margin-bottom: $spacing-unit;
  line-height: $spacing-unit * 4;
  overflow-wrap: break-word;
  border: 1px solid $text-color;
  border-radius: 3px;
}

.tag-delete {
  display: inline-block;
  margin-left: math.div($spacing-unit, 2);
  cursor: pointer;

  .mdi {
    line-height: 16px;
  }
}

.tag-form {
  :deep(.el-input__inner) {
    height: 34px;
  }
}

.tag-button {
  display: flex;
  align-items: center;
  margin-left: $spacing-unit;
}

.tag-button-show-input {
  display: flex;
  align-items: center;
}

.tag-input-form {
  display: flex;
  align-items: center;
}

.tag-input {
  max-width: $spacing-unit * 40;
}
</style>
